import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { useInfiniteHits, useInstantSearch } from "react-instantsearch";

import NoResultsMessage from "../../algolia_search/no_results_message";
import useInfiniteScroll from "../../hooks/use_infinite_scroll";

import EmptyResults from "./EmptyResults";
import MediaOutletCardGrid from "../card/MediaOutletCardGrid";
import MediaOutletCardList, { LIST_ACTION_COLUMN_WIDTH } from "../card/MediaOutletCardList";
import { listHeaderRowStyle } from "../../source_directory/utils";

function OutletCardSkeletonRow({ MediaOutletCardSkeleton }) {
  return (
    <>
      <div style={{ gridRow: "span 3" }}>
        <MediaOutletCardSkeleton />
      </div>
      <div className="d-none d-md-block" style={{ gridRow: "span 3" }}>
        <MediaOutletCardSkeleton />
      </div>
      <div className="d-none d-xl-block" style={{ gridRow: "span 3" }}>
        <MediaOutletCardSkeleton />
      </div>
    </>
  );
}

OutletCardSkeletonRow.propTypes = {
  MediaOutletCardSkeleton: PropTypes.elementType.isRequired,
};

function InfiniteHits({ MediaOutletCardSkeleton, viewMode = "grid" }) {
  const { status } = useInstantSearch();
  const isLoading = status === "stalled";

  const { hits, isLastPage, showMore } = useInfiniteHits();
  const hasMore = !isLastPage;

  const [lastTriggeredAt, scrollTriggerRef] = useInfiniteScroll({ hasMore });

  useEffect(() => {
    if (lastTriggeredAt > 0) showMore();
  }, [lastTriggeredAt, showMore]);

  if (viewMode === "list") {
    return (
      <>
        <NoResultsMessage customMessage={<EmptyResults />} hits={hits} />
        {hits.length > 0 && (
          <div style={{ ...listHeaderRowStyle, padding: "0 16px 8px" }}>
            <span style={{ flex: "0 0 240px" }}>Outlet</span>
            <span style={{ flex: "0 0 120px" }}>Media Users</span>
            <span style={{ flex: 1 }}>About</span>
            <span aria-hidden="true" style={{ flex: `0 0 ${LIST_ACTION_COLUMN_WIDTH}px` }} />
          </div>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {hits.map((hit) => (
            <MediaOutletCardList key={hit.objectID} hit={hit} />
          ))}
        </div>
        {hasMore && <div ref={scrollTriggerRef} />}
      </>
    );
  }

  return (
    <>
      <NoResultsMessage customMessage={<EmptyResults />} hits={hits} />
      <div className="sd-cards-grid">
        {isLoading && <OutletCardSkeletonRow MediaOutletCardSkeleton={MediaOutletCardSkeleton} />}
        {!isLoading && hits.map((hit) => <MediaOutletCardGrid key={hit.objectID} hit={hit} />)}
      </div>
      {hasMore && (
        <div ref={scrollTriggerRef} className="sd-cards-grid sd-cards-grid--loading-more">
          <OutletCardSkeletonRow MediaOutletCardSkeleton={MediaOutletCardSkeleton} />
        </div>
      )}
    </>
  );
}

InfiniteHits.propTypes = {
  MediaOutletCardSkeleton: PropTypes.elementType,
  viewMode: PropTypes.oneOf(["grid", "list"]),
};

export default InfiniteHits;
